import React, { useEffect } from 'react';
import Flex from '../../../components/Flex';
import { Center } from '../../../components/Center';
import { AbsenceOverview } from '../../../models/AbsenceProposal';
import { makePrioStyles } from '../../../theme/utils';
import PrioSpinner from '../../../components/PrioSpinner';
import { EmployeeId, OfficeId } from '../../../models/Types';
import StatBox from '../../../components/StatBox';
import { rounded } from '../../dashboard/components/util';
import { t } from 'i18next';
import { fetchEmployeeOverview } from '../api';
import { useQuery } from '@tanstack/react-query';

const useStyles = makePrioStyles((theme) => ({
  root: {
    height: '100%',
    overflow: 'hidden',
    position: 'relative',
    paddingTop: theme.spacing.regular,
  },
  statBox: {
    maxWidth: '250px',
    minHeight: '90px',
  },
  header: {
    paddingTop: theme.spacing.regular,
    paddingBottom: theme.spacing.small,
  },
  content: {
    height: '100%',
    overflow: 'auto',
    paddingRight: 2,
    gap: theme.spacing.large,
    '& h3': {
      color: theme.colors.base.blue[90],
    },
  },
  loadingOverlayAbsenceOverview: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: '#ffffffaa',
  },
  absenceListView: {
    borderTop: '1px solid rgb(240, 240, 240)',
    marginTop: theme.spacing.regular,
  },
  statBoxDescription: {
    color: theme.colors.application.typography.muted,
  },
}));

interface EmployeeAbsencesOverviewProps {
  employeeId: EmployeeId;
  absenceOverview: AbsenceOverview;
  absenceOverviewIsFetching;
  officeId?: OfficeId;
  fetchAbsenceOverview: () => void;
}

const EmployeeAbsencesOverview: React.FC<EmployeeAbsencesOverviewProps> = (
  props
) => {
  //#region ------------------------------ Defaults
  const classes = useStyles();
  const { absenceOverviewIsFetching, fetchAbsenceOverview, employeeId } = props;
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  //#endregion

  //#region ------------------------------ Methods / Handlers
  //#endregion

  //#region ------------------------------ Effects
  useEffect(() => {
    if (fetchAbsenceOverview) {
      fetchAbsenceOverview();
    }
  }, [fetchAbsenceOverview]);

  const { data: employeeAbsenceData } = useQuery({
    queryKey: ['employeeAbsenceData', employeeId],
    queryFn: async () => {
      const searchString = `EmployeeId eq '${employeeId}'`;
      const { data } = await fetchEmployeeOverview(searchString);
      if (data?.items?.length > 0) {
        return data.items[0];
      } else {
        console.warn('No valid data returned from fetchEmployeeOverview');
        return null;
      }
    },
    staleTime: 60000,
  });
  //#endregion

  return (
    <div className={classes.root}>
      <Flex.Column className={classes.content}>
        <h2 className={classes.header}>
          {t('hr:timeAndLeaveManagement.overview.yearSummary', {
            year: new Date().getFullYear(),
            defaultValue:
              'Übersicht über das Jahr {{year}} - Zusammenfassung von Arbeitszeiten und Abwesenheiten',
          })}
        </h2>
        <Flex.Row childrenGap={136}>
          <Flex.Column flex={12}>
            <h3>
              {t('hr:personnelFile.absenceOverview.subTitle.vacationDays')}
            </h3>
            <Flex.Row childrenGap={8}>
              <StatBox
                className={classes.statBox}
                title={t(
                  'hr:personnelFile.absenceOverview.remainingLeaveLastYear',
                  {
                    lastYear: new Date().getFullYear() - 1,
                  }
                )}
                value={`${employeeAbsenceData?.calculated?.remainingLeaveLastYear ?? 0} ${t('common:calendar.days')}`}
              />
              <StatBox
                className={classes.statBox}
                title={t(
                  'hr:personnelFile.absenceOverview.holidayEntitlements'
                )}
                value={`${employeeAbsenceData?.calculated?.holidayEntitlements ?? 0} ${t('common:calendar.days')}`}
              />
              <StatBox
                className={classes.statBox}
                title={t('hr:personnelFile.absenceOverview.remainingLeave')}
                value={`${employeeAbsenceData?.calculated?.remainingLeave ?? 0} ${t('common:calendar.days')}`}
              />
            </Flex.Row>
          </Flex.Column>
        </Flex.Row>

        <Flex.Row childrenGap={12}>
          <Flex.Column flex={12}>
            <h3>{t('hr:personnelFile.absenceOverview.subTitle.overtime')}</h3>
            <Flex.Row childrenGap={8}>
              <StatBox
                className={classes.statBox}
                title={t('hr:personnelFile.absenceOverview.currentOvertime')}
                value={`${rounded(employeeAbsenceData?.calculated?.currentOvertime ?? 0)} h`}
              />
              <StatBox
                className={classes.statBox}
                title={t(
                  'hr:personnelFile.absenceOverview.unsettledOvertimeHours'
                )}
                value={`${rounded(employeeAbsenceData?.calculated?.unsettledOvertimeHours ?? 0)} h`}
                descriptionClassName={classes.statBoxDescription}
              />
              <StatBox
                className={classes.statBox}
                title={t('hr:personnelFile.absenceOverview.paidOvertime')}
                value={`${rounded(employeeAbsenceData?.calculated?.paidOvertime ?? 0)} h`}
                description={t(
                  'hr:personnelFile.absenceOverview.description.paidOut'
                )}
                descriptionClassName={classes.statBoxDescription}
              />

              <StatBox
                className={classes.statBox}
                title={t(
                  'hr:personnelFile.absenceOverview.overtimeCompensationCurrentYear'
                )}
                value={`${rounded(employeeAbsenceData?.calculated?.overtimeCompensationCurrentYear ?? 0)} ${t('common:calendar.days')}`}
                description={t(
                  'hr:personnelFile.absenceOverview.description.absence'
                )}
                descriptionClassName={classes.statBoxDescription}
              />
            </Flex.Row>
          </Flex.Column>
        </Flex.Row>

        <Flex.Row childrenGap={12}>
          <Flex.Column flex={12}>
            <h3>
              {t('hr:personnelFile.absenceOverview.subTitle.otherAbsences')}
            </h3>
            <Flex.Row childrenGap={8}>
              <StatBox
                className={classes.statBox}
                title={t('hr:personnelFile.absenceOverview.sickDays')}
                value={`${employeeAbsenceData?.calculated?.sickDaysCurrentYear ?? 0} ${t('common:calendar.days')}`}
              />
              <StatBox
                className={classes.statBox}
                title={t('hr:personnelFile.absenceOverview.parentalLeaveDays')}
                value={`${employeeAbsenceData?.calculated?.parentalLeaveDays ?? 0} ${t('common:calendar.days')}`}
              />
              <StatBox
                className={classes.statBox}
                title={t('hr:personnelFile.absenceOverview.schoolOrStudyDays')}
                value={`${employeeAbsenceData?.calculated?.schoolOrStudyDays ?? 0} ${t('common:calendar.days')}`}
              />
              <StatBox
                className={classes.statBox}
                title={t('hr:personnelFile.absenceOverview.specialLeaveDays')}
                value={`${employeeAbsenceData?.calculated?.paidSpecialLeaveDays ?? 0} ${t('common:calendar.days')}`}
              />
            </Flex.Row>
          </Flex.Column>
        </Flex.Row>
      </Flex.Column>

      {absenceOverviewIsFetching && (
        <div className={classes.loadingOverlayAbsenceOverview}>
          <Center>
            <PrioSpinner size="large" />
          </Center>
        </div>
      )}
    </div>
  );
};

export default EmployeeAbsencesOverview;
